/**
 * ComentariosSection · seccion COMENTARIOS del ActionCenterDetailPanel.
 *
 * 0675 F3 piloto multi-modelo #2 · BACKLOG [0666b][DETAIL-COMMENTS].
 * Timeline cronologico DESC (mas recientes arriba). Texto plano hasta 5000 chars.
 * NO threading · NO edicion (scope creep evitado · ver plan mode previo decisiones #4 y #5).
 */

import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { useI18n } from '../../../../i18n';
import { PersonaChip } from '../../../shared/components/PersonaChip';
import type { ArtefactoWorkflowTipo } from '../hooks/useActionCenterDetail';
import {
  formatTiempoRelativo,
  type TiempoRelativoLabels,
  useArtefactoComentarios,
  useCrearComentario,
  useEliminarComentario,
} from '../hooks/useArtefactoComentarios';
import styles from './ComentariosSection.module.css';

// Mapping locale i18n V·IAnet (es/en/zh) -> BCP-47 navegador
const LOCALE_BCP47: Record<string, string> = {
  es: 'es-ES',
  en: 'en-US',
  zh: 'zh-CN',
};

const CONTENIDO_MAX = 5000;

export interface ComentariosSectionProps {
  artefactoTipo: ArtefactoWorkflowTipo;
  artefactoId: string;
}

export function ComentariosSection(props: ComentariosSectionProps) {
  const { t, lang } = useI18n();
  const tt = t.sign.actionCenter.premium.comentarios;

  // Fix MED-2 Codex 0675: labels i18n para tiempo relativo (antes hardcoded en es).
  const tiempoLabels: TiempoRelativoLabels = useMemo(
    () => ({
      ahora: tt.tiempoAhora,
      haceMinutos: tt.tiempoMinutos,
      haceHoras: tt.tiempoHoras,
      haceDias: tt.tiempoDias,
    }),
    [tt],
  );
  const localeBcp47 = LOCALE_BCP47[lang] ?? 'es-ES';

  const [borrador, setBorrador] = useState('');
  const [error, setError] = useState<string | null>(null);

  const comentariosQuery = useArtefactoComentarios(props.artefactoTipo, props.artefactoId);
  const crearMut = useCrearComentario(props.artefactoTipo, props.artefactoId);
  const eliminarMut = useEliminarComentario(props.artefactoTipo, props.artefactoId);

  const comentarios = comentariosQuery.data ?? [];
  const trimLength = borrador.trim().length;
  const tooLong = borrador.length > CONTENIDO_MAX;
  const puedeEnviar = trimLength > 0 && !tooLong && !crearMut.isPending;

  const handleEnviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!puedeEnviar) return;
    setError(null);
    try {
      await crearMut.mutateAsync(borrador.trim());
      setBorrador('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : tt.errorCrear;
      setError(msg);
    }
  };

  const handleEliminar = async (comentarioId: string) => {
    if (!window.confirm(tt.confirmarEliminar)) return;
    setError(null);
    try {
      await eliminarMut.mutateAsync(comentarioId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : tt.errorEliminar;
      setError(msg);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setBorrador(e.target.value);
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleEnviar}>
        <textarea
          className={styles.textarea}
          value={borrador}
          onChange={handleChange}
          placeholder={tt.placeholder}
          disabled={crearMut.isPending}
          aria-label={tt.placeholder}
          maxLength={
            CONTENIDO_MAX + 1000
          } /* permite ver overflow visualmente · validacion logica vive en puedeEnviar */
        />
        <div className={styles.formActions}>
          <span
            className={`${styles.contador} ${tooLong ? styles.contadorExceso : ''}`}
            aria-live="polite"
          >
            {borrador.length} / {CONTENIDO_MAX}
          </span>
          <button
            type="submit"
            className={styles.btnEnviar}
            disabled={!puedeEnviar}
            aria-label={tt.enviar}
          >
            {crearMut.isPending ? tt.enviando : tt.enviar}
          </button>
        </div>
      </form>

      {error && <div className={styles.error}>{error}</div>}

      {comentariosQuery.isLoading && <p className={styles.cargando}>{tt.cargando}</p>}

      {!comentariosQuery.isLoading && comentarios.length === 0 ? (
        <p className={styles.vacio}>{tt.listaVacia}</p>
      ) : (
        <ul className={styles.timeline}>
          {comentarios.map((c) => (
            <li key={c.id} className={styles.item}>
              <div className={styles.itemHeader}>
                {/* 0677: reemplaza subidoPor.slice(0,8) por PersonaChip reusable */}
                <PersonaChip userId={c.subidoPor} />
                <span className={styles.itemTiempo}>
                  {formatTiempoRelativo(c.creadoEn, tiempoLabels, localeBcp47)}
                </span>
              </div>
              <div className={styles.bubble}>
                {c.contenido}
                <div className={styles.itemAcciones}>
                  <button
                    type="button"
                    className={`${styles.btnIcon} ${styles.btnIconDanger}`}
                    onClick={() => void handleEliminar(c.id)}
                    disabled={eliminarMut.isPending}
                    title={tt.eliminar}
                    aria-label={tt.eliminar}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
